import type { BarColumnCapabilities } from '../../../chart-components/unified-bar-column-chart/unified-bar-column-chart.types';
import { toNumberScale } from '../../dvm-layout-builder/dvm-builder-number-format.utils';

/**
 * Fold the Format accordion / tile Format popup values into bar & column
 * capabilities. Pure: used by the step at Generate / preview time and by the
 * shell when the Format popup of an existing tile changes.
 */
export function applyBarFormatToCapabilities(
capabilities: BarColumnCapabilities,
format: Record<string, unknown> | undefined,
resolvePaletteColors: (paletteKey?: string) => string[] | undefined,
): BarColumnCapabilities {
  const f = format || {};
  capabilities.showValues = f['showValues'] !== false;
  capabilities.numberFormat = String(f['numberFormat'] ?? 'NONE');
  capabilities.numberScale = f['numberScale'] ? String(f['numberScale']) : toNumberScale(f['numberFormat']);
  capabilities.decimals = typeof f['decimals'] === 'number' ? (f['decimals'] as number) : Number(f['decimals'] ?? 1);
  const affix = (key: string): string | undefined => (f[key] !== undefined && f[key] !== null ? String(f[key]) : undefined);
  capabilities.xAxisPrefix = affix('xAxisPrefix');
  capabilities.xAxisSuffix = affix('xAxisSuffix');
  capabilities.yAxisPrefix = affix('yAxisPrefix');
  capabilities.yAxisSuffix = affix('yAxisSuffix');
  capabilities.labelMode = f['labelMode'] ? String(f['labelMode']) : undefined;
  capabilities.showLegend = f['showLegend'] !== false;
  capabilities.legendPosition = f['legendPosition'] ? String(f['legendPosition']) : undefined;
  capabilities.palette = f['palette'] ? String(f['palette']) : undefined;
  capabilities.paletteColors = resolvePaletteColors(capabilities.palette);
  capabilities.colors = undefined;
  capabilities.xAxisLabel = (f['xAxisLabel'] as string) || undefined;
  capabilities.yAxisLabel = (f['yAxisLabel'] as string) || undefined;
  capabilities.xAxisLabel2 = (f['xAxisLabel2'] as string) || undefined;
  capabilities.yAxisLabel2 = (f['yAxisLabel2'] as string) || undefined;
  capabilities.axisLabelRotation = f['axisLabelRotation'] ? String(f['axisLabelRotation']) : undefined;
  capabilities.showGridLines = f['showGridLines'] !== false;
  capabilities.tooltipEnabled = f['tooltipEnabled'] !== false;
  capabilities.tooltipTrigger = f['tooltipTrigger'] ? String(f['tooltipTrigger']) : undefined;
  capabilities.tooltipPosition = f['tooltipPosition'] ? String(f['tooltipPosition']) : undefined;
  capabilities.tooltipShowChart = f['tooltipShowChart'] === true;
  capabilities.tooltipAxisPointer = f['tooltipAxisPointer'] ? String(f['tooltipAxisPointer']) : undefined;
  capabilities.tooltipBackground = (f['tooltipBackground'] as string) || undefined;
  capabilities.tooltipTextColor = (f['tooltipTextColor'] as string) || undefined;
  capabilities.tooltipBorderColor = (f['tooltipBorderColor'] as string) || undefined;
  if (f['tooltipBorderRadius'] !== undefined) capabilities.tooltipBorderRadius = Number(f['tooltipBorderRadius']);
  if (f['tooltipFontSize'] !== undefined) capabilities.tooltipFontSize = Number(f['tooltipFontSize']);
  capabilities.fontFamily = f['fontFamily'] ? String(f['fontFamily']) : undefined;
  capabilities.fontSize = Number(f['fontSize']) > 0 ? Number(f['fontSize']) : undefined;
  capabilities.axisFontWeight = f['axisFontWeight'] ? String(f['axisFontWeight']) : undefined;
  if (f['barWidthPercent'] !== undefined) capabilities.barWidthPercent = Number(f['barWidthPercent']);
  capabilities.smoothLines = f['smoothLines'] === true;
  capabilities.showVariance = f['showVariance'] !== false;
  capabilities.varianceUpColor = (f['varianceUpColor'] as string) || undefined;
  capabilities.varianceDownColor = (f['varianceDownColor'] as string) || undefined;
  if (f['barGapPercent'] !== undefined) capabilities.barGapPercent = Number(f['barGapPercent']);
  if (f['barCategoryGapPercent'] !== undefined) capabilities.barCategoryGapPercent = Number(f['barCategoryGapPercent']);
  if (f['barCornerRadius'] !== undefined) capabilities.barCornerRadius = Number(f['barCornerRadius']);
  capabilities.sortBy = f['sortBy'] ? String(f['sortBy']) : undefined;
  capabilities.topN = f['topN'] !== undefined && f['topN'] !== '' && f['topN'] !== null ? Number(f['topN']) : undefined;
  capabilities.valueAxisMin = f['valueAxisMin'] !== undefined && f['valueAxisMin'] !== '' && f['valueAxisMin'] !== null ? Number(f['valueAxisMin']) : undefined;
  capabilities.valueAxisMax = f['valueAxisMax'] !== undefined && f['valueAxisMax'] !== '' && f['valueAxisMax'] !== null ? Number(f['valueAxisMax']) : undefined;
  capabilities.valueAxisLog = f['valueAxisLog'] === true;
  capabilities.showStackTotals = f['showStackTotals'] === true;
  capabilities.animation = f['animation'] !== false;
  capabilities.enableZoom = f['enableZoom'] === true;
  capabilities.zoomAxis = f['zoomAxis'] ? String(f['zoomAxis']) : undefined;
  capabilities.showZoomSlider = f['showZoomSlider'] !== false;
  capabilities.highlightOnClick = f['highlightOnClick'] === true;
  capabilities.crossFilter = f['crossFilter'] === true;
  capabilities.emitSelection = f['emitSelection'] === true;
  return capabilities;
}
